import { useCallback, useEffect, useRef, useState } from 'react';
import { isEntitlementDenied, onEntitlementRequired } from 'Utils/entitlementGate';

const browserIsOnline = (): boolean =>
  typeof navigator === 'undefined' || navigator.onLine !== false;

/**
 * MUS-651: whether a lesson screen may show its "didn't load" message. Not
 * while the device is offline (the offline screen says why) and not once the
 * server has denied the lesson for want of a subscription (the paywall says
 * why); either one covering the screen is the way forward, and the message
 * would only paint over it.
 *
 * `noteFailure` lets a caller hand over the error a load gave up on, so an
 * entitlement denial counts even where the paywall signal was not seen.
 *
 * The denial is held only until the account's subscription status changes:
 * in the apps a purchase happens with the lesson still mounted, the paywall
 * closes, and the lesson's message (whose Retry asks again) must come back.
 */
export const useDidNotLoadMessageAllowed = (
  subscriptionStatus?: string | null,
): {
  allowed: boolean;
  noteFailure: (error: unknown) => void;
} => {
  const [online, setOnline] = useState(browserIsOnline);
  const [entitlementDenied, setEntitlementDenied] = useState(false);
  const seenSubscriptionStatus = useRef(subscriptionStatus);

  useEffect(() => {
    if (seenSubscriptionStatus.current === subscriptionStatus) return;
    seenSubscriptionStatus.current = subscriptionStatus;
    setEntitlementDenied(false);
  }, [subscriptionStatus]);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    setOnline(browserIsOnline());
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => onEntitlementRequired(() => setEntitlementDenied(true)), []);

  const noteFailure = useCallback((error: unknown) => {
    if (isEntitlementDenied(error)) setEntitlementDenied(true);
  }, []);

  return { allowed: online && !entitlementDenied, noteFailure };
};
